/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from 'react';

import { Skeleton, SkeletonProps } from '@material-ui/lab';

import Settings from '../conf/settings';
import {
  Api,
  ApiRatings,
  Comments,
  Document,
  Documents,
  EndpointInfo,
  EndpointURL,
  GraphqlResponse,
  Key,
  KeyList,
  SettingsInterface,
  StorageOperations,
  SubscriptionList,
  User,
} from '../data/interfaces';
import * as Constants from '../data/constants';
import { ADMIN_SCOPE, PATH_SEPARATOR, PRODUCTION_KEY_MANAGE_SCOPE, SANDBOX_KEY_MANAGE_SCOPE, SUBSCRIBE_SCOPE } from '../data/constants';

export const isValidData = (
  data:
    | Api
    | Documents
    | Comments
    | ApiRatings
    | SubscriptionList
    | User
    | Document
    | { [key: string]: string }
):
  | Api
  | Documents
  | Comments
  | ApiRatings
  | SubscriptionList
  | User
  | Document
  | { [key: string]: string }
  | null => {
  if (data && Object.keys(data).length !== 0) {
    return data;
  } else {
    return null;
  }
};

export const renderSkeleton = (
  type: 'text' | 'rect' | 'circle' = 'rect',
  width: number | string = '100%',
  height: number | string = '100%',
  skeletonProps?: SkeletonProps,
) => {
  return <Skeleton variant={type} width={width} height={height} {...skeletonProps} />;
};

export const getApiTagsEndpoint = (): string => {
  const apimHostname = Settings.apimApi;
  const tagResource = Settings.devportalContext + Constants.RESOURCE_TAGS;

  return new URL(tagResource, apimHostname).toString();
};

export const getApiCategoryEndpoint = (): string => {
  const apimHostname = Settings.apimApi;
  const tagResource = Settings.devportalContext + Constants.RESOURCE_CATEGORIES;

  return new URL(tagResource, apimHostname).toString();
};

export const getApiListEndpoint = (pagination?: string): string => {
  const apimHostname = Settings.apimApi;
  const apiResource = pagination
    ? Settings.devportalContext + pagination
    : Settings.devportalContext + Constants.RESOURCE_API;

  return new URL(apiResource, apimHostname).toString();
};

export const getCustomDomainOrgHandleMappingEndpoint = (): string => {
  const apiServiceHost = Settings.apiServiceHost;
  const customDomainOrgHandleMappingResource =
      Constants.RESOURCE_CUSTOM_DOMAIN +
      Constants.PATH_DEVPORTAL +
      Constants.RESOURCE_CUSTOM_DOMAIN_MAPPINGS;

  return new URL(customDomainOrgHandleMappingResource, apiServiceHost).toString();
};

export const getOrganizationMetadataEndpoint = (): string => {
  const apiServiceHost = Settings.apiServiceHost;
  const organizationMetadataResource = Constants.RESOURCE_METADATA;

  return new URL(organizationMetadataResource, apiServiceHost).toString();
};

export const getApiResourceEndpoint = (
  apiId: string,
  resource = ''
): string => {
  const apimHostname = Settings.apimApi;
  const apiResource =
    Settings.devportalContext +
    Constants.RESOURCE_API +
    Constants.PATH_SEPARATOR +
    apiId +
    resource;

  return new URL(apiResource, apimHostname).toString();
};

export const getSubscriptionResourceEndpoint = (
  subscriptionId = '',
  resource = ''
): string => {
  const apimHostname = Settings.apimApi;
  const subscriptionResource =
    Settings.devportalContext +
    Constants.RESOURCE_SUBSCRIPTIONS +
    Constants.PATH_SEPARATOR +
    subscriptionId +
    resource;

  return new URL(subscriptionResource, apimHostname).toString();
};

export const getApplicationEndpoint = (appId = '', resource = ''): string => {
  const apimHostname = Settings.apimApi;
  const appResource =
    Settings.devportalContext +
    Constants.RESOURCE_APPLICATIONS +
    Constants.PATH_SEPARATOR +
    appId +
    resource;

  return new URL(appResource, apimHostname).toString();
};

export const getApiKeysEndpoint = (apiId = '', applicationId = '', organization = '', keyType = ''): string => {
  const apimHostname = Settings.apimApi;
  const apiKeysResource =
    Settings.devportalContext +
    "/api-keys";
  const url = new URL(apiKeysResource, apimHostname);
  if (apiId != '') {
    url.searchParams.append('apiId', apiId);
  }
  if (applicationId != '') {
    url.searchParams.append('applicationId', applicationId);
  }
  url.searchParams.append('organizationId', organization);
  url.searchParams.append('keyType', keyType);
  return url.toString();
}

export const getApiKeyGenerationV2Endpoint = ():string => {
  const apimHostname = Settings.apimApi;
  const apiKeysResource =
    Settings.devportalContext +
    "/api-keys/generate";
  return new URL(apiKeysResource, apimHostname).toString();
}

export const getApiKeyRegenerationV2Endpoint = (apiKeyId: string):string => {
  const apimHostname = Settings.apimApi;

  const apiKeysResource =
    Settings.devportalContext +
    "/api-keys/" + apiKeyId + "/regenerate";
  return new URL(apiKeysResource, apimHostname).toString();
}

export const getApiKeyRovokeV2Endpoint = (apiKeyId: string):string => {
  const apimHostname = Settings.apimApi;

  const apiKeysResource =
    Settings.devportalContext +
    "/api-keys/" + apiKeyId + "/revoke";
  return new URL(apiKeysResource, apimHostname).toString();
}

export const getApplicationShareEndpoint = (appId = '', resource = ''): string => {
  const apimHostname = Settings.apimApi;
  const appResource =
    Settings.devportalContext +
    Constants.RESOURCE_APPLICATIONS +
    Constants.PATH_SEPARATOR +
    appId +
    resource;

  return new URL(appResource, apimHostname).toString();
};

export const getApiKeyGenerationEndpoint = (
  appId: string,
  environment: string
): string => {
  const apimHostname = Settings.apimApi;
  const appResource =
    Settings.devportalContext +
    Constants.RESOURCE_APPLICATIONS +
    Constants.PATH_SEPARATOR +
    appId +
    Constants.RESOURCE_API_KEY +
    Constants.PATH_SEPARATOR +
    environment +
    Constants.RESOURCE_GENERATE;
  return new URL(appResource, apimHostname).toString();
};

export const getApiTestKeyGenerationEndpoint = (
  apiId: string,
): string => {
  const apimHostname = Settings.apimApi;
  const appResource =
    Settings.devportalContext +
    Constants.RESOURCE_API +
    Constants.PATH_SEPARATOR +
    apiId +
    Constants.RESOURCE_GENERATE_TEST_KEY
  return new URL(appResource, apimHostname).toString();
}

export const getSdkResourceEndpoint = (): string => {
  const apimHostname = Settings.apimApi;
  const appResource =
    Settings.devportalContext + Constants.RESOURCE_LANGUAGE_SDK;

  return new URL(appResource, apimHostname).toString();
};

export const getSubscriptionThrottlingPoliciesEndpoint = (): string => {
  const apimHostname = Settings.apimApi;
  const throttlingPolicyResource =
    Settings.devportalContext +
    Constants.RESOURCE_THROTTLING_POLICIES +
    Constants.PATH_SEPARATOR +
    Constants.SUBSCRIPTION_POLICY_TYPE;
  return new URL(throttlingPolicyResource, apimHostname).toString();
};

export const getThrottlingPolicyEndpoint = (
  policyLevel = '',
  policyName = ''
): string => {
  const apimHostname = Settings.apimApi;
  const throttlingPolicyResource =
    Settings.devportalContext +
    Constants.RESOURCE_THROTTLING_POLICIES +
    Constants.PATH_SEPARATOR +
    policyLevel +
    Constants.PATH_SEPARATOR +
    policyName;
  return new URL(throttlingPolicyResource, apimHostname).toString();
};

export const getClientHost = (): string => {
  const clientHost = Settings.clientHost;

  return new URL(clientHost).toString();
};

/*
 * Returns a settings object after overriding the default settings by the config settings
 * @param windowSettings    Settings object from devportal.config.js
 * @param defaultSettings   Settings object from settings.ts
 * @return SettingsInterface
 */
export const getApplicationSettings = (
  windowSettings: SettingsInterface,
  defaultSettings: SettingsInterface
): SettingsInterface => {
  const applicationSettingsMap: Record<string, unknown> = {};
  const windowSettingsMap: Record<string, unknown> = {};

  // Converts the windowSettings object to a map
  for (const [key, value] of Object.entries(windowSettings)) {
    windowSettingsMap[key] = value;
  }

  // Overrides the default settings by devportal.config.js
  for (const [key, value] of Object.entries(defaultSettings)) {
    if (key in windowSettingsMap) {
      applicationSettingsMap[key] = windowSettingsMap[key];
    } else {
      applicationSettingsMap[key] = value;
    }
  }
  return JSON.parse(JSON.stringify(applicationSettingsMap));
}

export const sessionStoreOperation = (
  operation: StorageOperations,
  key: string,
  value = ''
) => {
  switch (operation) {
    case StorageOperations.ADD:
      sessionStorage.setItem(key, value);
      break;
    case StorageOperations.REMOVE:
      sessionStorage.removeItem(key);
      break;
    case StorageOperations.GET:
      return sessionStorage.getItem(key);
  }
};

export const localStoreOperation = (
  operation: StorageOperations,
  key: string,
  value = ''
) => {
  switch (operation) {
    case StorageOperations.ADD:
      localStorage.setItem(key, value);
      break;
    case StorageOperations.REMOVE:
      localStorage.removeItem(key);
      break;
    case StorageOperations.GET:
      return localStorage.getItem(key);
  }
};

export function groupObjectsByKey(objectArray: Document[], property: string) {
  return objectArray.reduce((accumulator: any, object: any) => {
    const key: string = object[property];
    if (!accumulator[key]) {
      accumulator[key] = [];
    }
    // Add object to list for given key's value
    accumulator[key].push(object);
    return accumulator;
  }, {});
}

export const getUserInfoEndpoint = (): string => {
  const apimHostname = Settings.idpHost;
  const userInfoResource = Settings.userInfo;

  return new URL(userInfoResource, apimHostname).toString();
};

export const getGraphqlEndpoint = (): string => {
  const graphqlHostname = Settings.graphqlServiceHost;
  const graphResource = Settings.graphqlResource;

  return new URL(graphResource, graphqlHostname).toString();
};

export const getAzureCDNEndpoint = (organization: string ): string => {
  const azureCDNHostname = Settings.azureCDNOrigin;
  return new URL(`${organization}/default.json`, azureCDNHostname).toString();
};

export const getUserValidationEndpoint = (): string => {
  const apiServiceHost = Settings.apiServiceHost;
  const userOrgsResource = Settings.validateUserEndpoint;

  return new URL(userOrgsResource, apiServiceHost).toString();
};

export const getAddEnterpriseUserEndpoint = (): string => {
  const apiServiceHost = Settings.apiServiceHost;
  const userOrgsResource = Settings.addEnterpriseUserEndpoint;

  return new URL(userOrgsResource, apiServiceHost).toString();
};

export const getGetInvitationEndpoint = (
  confirmationOrg: string,
  confirmationKey: string
): string => {
  const apiServiceHost = Settings.apiServiceHost;

  return new URL(
    `/user-mgt/1.0.0/orgs/${confirmationOrg}/v2/invitations/${confirmationKey}`,
    apiServiceHost
  ).toString();
};

export const getAcceptInvitationEndpoint = (
  confirmationOrg: string,
  confirmationKey: string
): string => {
  const apiServiceHost = Settings.apiServiceHost;

  return new URL(
    `/user-mgt/1.0.0/orgs/${confirmationOrg}/v2/invitations/${confirmationKey}`,
    apiServiceHost
  ).toString();
};

export const getFormatteddDateTime = (stringDate: string) => {
  // stringDate format 'YYYY-MM-DD  HH:MM:SS.MS'
  if (stringDate) {
    const utcDate = stringDate + 'Z'; // Z for UTC timezone offset
    const date = new Date(utcDate);
    const formattedDate = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
    return formattedDate + ' ' + formattedTime;
  }
  return stringDate;
};

export const getTimeDurationForDate = (stringDate: string) => {
  // stringDate format 'YYYY-MM-DD  HH:MM:SS.MS'
  // This method is written with the assumption that the time zone of 'stringDate' param passed in, is in UTC.
  // Otherwise, proper timezone correction should be made to the passed in date
  // Eg:  If 'stringDate' is in Pacific Time, then Pacific Time = GMT-08:00
  //      stringDate = stringDate + "-08:00"
  if (stringDate) {
    const utcDate = stringDate + 'Z'; // Z for UTC timezone offset
    let dateTimeDifference: number =
      new Date().getTime() - new Date(utcDate).getTime();
    if (dateTimeDifference < 0) {
      dateTimeDifference = 0;
    }
    // (1000 * 60 * 60 * 24) => (Miliseconds * Seconds * Minutes * Hours)
    const numOfDays = Math.round(dateTimeDifference / (1000 * 60 * 60 * 24));
    if (numOfDays > 0) {
      return stringDate.substring(0, stringDate.indexOf('.'));
    }
    const numOfHours = Math.round(dateTimeDifference / (1000 * 60 * 60));
    if (numOfHours > 0) {
      return numOfHours === 1 ? '1 hour ago' : numOfHours + ' hours ago';
    }
    const numOfMinutes = Math.round(dateTimeDifference / (1000 * 60));
    if (numOfMinutes > 0) {
      return numOfMinutes === 1
        ? '1 minute ago'
        : numOfMinutes + ' minutes ago';
    }
    const numOfSeconds = Math.round(dateTimeDifference / 1000);
    return numOfSeconds === 1 ? '1 second ago' : numOfSeconds + ' seconds ago';
  } else {
    return 'Invalid date';
  }
};

export const getApiGatewayUrl = (apiContext: string, organization: string) => {
  const contextArray = apiContext.split(Constants.PATH_SEPARATOR);
  const apimGatewayHostSuffix =
    Settings.apimGatewayHostSuffix || Constants.DEFAULT_API_GATEWAY_SUFFIX;
  const host =
    organization === Constants.CONTROL_PLANE_ORG
      ? Constants.CP_GATEWAY_ENV_HOST
      : apimGatewayHostSuffix;

  return (
    Constants.PROTOCOL_HTTPS +
    contextArray[1] +
    host +
    Constants.PATH_SEPARATOR +
    contextArray.slice(2).join(Constants.PATH_SEPARATOR)
  );
};

export const getAllApiGatewayUrls = (
  endpointUrls: EndpointURL[],
  graphqlInfo: GraphqlResponse,
  isLatestVersion: boolean,
  version: string,
  isPublicView: boolean,
  selectedOrg?: any
): EndpointInfo[] => {
  const graphqlEnvArray = graphqlInfo?.environments;
  const isPdpDomainCustomizedOrg =  Settings.pdpDomainCustomizedOrgs.includes(selectedOrg?.uuid);

  if (isPublicView) {
    const endpointInfo = endpointUrls?.filter((endpointEnv) => {
      return (endpointEnv.environmentName === 'production-us-east-azure' ||
          endpointEnv.environmentName === 'production-eu-north-azure' ||
          (endpointEnv.environmentName === 'prod' && // temporary fix for env named 'prod' for WDT
              (endpointEnv?.URLs?.http.startsWith('http://apis.wso2.com/') || 
              endpointEnv?.URLs?.https.startsWith('https://apis.wso2.com/'))));
    })?.map((endpoint) => {
      if (endpoint?.URLs.https || endpoint?.URLs.wss) {
        let protocol = Constants.PROTOCOL_HTTPS;
        let tmpUrl = "";
        if (endpoint?.URLs.https) {
          tmpUrl = endpoint.URLs.https.replace(
            Constants.PROTOCOL_HTTPS,
            ''
          );
        }
        if (endpoint?.URLs.wss) {
          tmpUrl = endpoint.URLs.wss.replace(
            Constants.PROTOCOL_WSS,
            ''
          );
          protocol = Constants.PROTOCOL_WSS;
        }
        // for dynamically created environments, the orgUUID is already included in urlParts[0].
        const ORG_UUID_WITH_DOMAIN_REGEX =
            /^([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})(-).*$/;

          if (
            !isPdpDomainCustomizedOrg &&
            selectedOrg?.handle !== Settings.choreoSystemOrg
          ) {
            // for custom urls and urls with custom domains
            if (
              !ORG_UUID_WITH_DOMAIN_REGEX.test(
                tmpUrl.trim().split(Constants.PATH_SEPARATOR)[0]
              ) &&
              endpoint.environmentDisplayName !== "Sandbox"
            ) {
              return {
                url: `${protocol}${tmpUrl}`,
                environment: endpoint?.environmentName,
                environmentDisplayName: getEnvironmentEndpointDisplayName(
                  endpoint?.environmentDisplayName
                ),
                choreoEnvironmentName: endpoint?.choreoEnvironmentName,
                accessibilityType: endpoint?.accessibilityType,
                isSandboxEndpoint: endpoint?.isSandboxEndpoint,
                isCritical: endpoint?.isCritical,
                isSandbox: endpoint?.isSandbox,
              } as EndpointInfo;
            }
          }

        const urlParts = tmpUrl.trim().split(Constants.PATH_SEPARATOR);

        let hostSuffix = `${urlParts[1]}-${urlParts[0]}`;
        const ORGANIZATION_UUID_REGEX=
            /^([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})$/;
        if (ORG_UUID_WITH_DOMAIN_REGEX.test(urlParts[0])) {
          hostSuffix = urlParts[0];
        } else {
          // Related issue for prod sandbox fix: https://github.com/wso2-enterprise/choreo/issues/26120
          const PROD_SANDBOX_URL_REGEX = /^(sandbox)\.(.*)$/;
          if (PROD_SANDBOX_URL_REGEX.test(urlParts[0])) {
            urlParts[0] = urlParts[0].replace(PROD_SANDBOX_URL_REGEX, 'prod-sandbox.$2');
          }
          hostSuffix = `${urlParts[1]}-${urlParts[0]}`;
        }
        // Set the starting position of the urlParts array to generate the context.
        // The extracted urlParts will have the format of,
        // ['<gateway-domain>','<org-uuid>','<project-handler>','<context-part-1>',...,'<context-part-n>','<version>']
        // Since we are already using 0th and 1st index to build the domain, others parts will belong to the context starting from 2nd idx.
        let urlContextStartIdx = 2;
        if (selectedOrg?.handle === Settings.choreoSystemOrg) {
          hostSuffix = Settings.choreoSystemHostSuffix;
          // The choreo system organization is a special case where we don't need the <project-handler> in the invoke url.
          // Hence, we will start from 3rd index to build the remaning context.
          // Refer https://github.com/wso2-enterprise/choreo/issues/12188 for more info.
          urlContextStartIdx = 3;
        } else if (!ORGANIZATION_UUID_REGEX.test(urlParts[1])) {
          //If the endpoint URL has the format  <Protocol>://<gateway-domain>/<project-handler>/'<context-part-1>',...,'<context-part-n>/<version>'
          //The extracted urlParts will have the format of,
          //['<gateway-domain>','<project-handler>','<context-part-1>',...,'<context-part-n>','<version>']
          //Build remaining context starting from the 1st index
          urlContextStartIdx = 1;
        }

        const gwURL = `${protocol}${hostSuffix}/${urlParts
            .slice(urlContextStartIdx)
            .join(Constants.PATH_SEPARATOR)}`;
          return {
            url: gwURL,
            environment: endpoint?.environmentName,
            environmentDisplayName: getEnvironmentEndpointDisplayName(
              endpoint?.environmentDisplayName
            ),
            choreoEnvironmentName: endpoint?.choreoEnvironmentName,
            accessibilityType: endpoint?.accessibilityType,
            isSandboxEndpoint: endpoint?.isSandboxEndpoint,
            isCritical: endpoint?.isCritical,
            isSandbox: endpoint?.isSandbox,
          } as EndpointInfo;
        }
        return null;
      })
      // This will removed the null values from the array
      .filter((endpoint) => endpoint !== null);
    return endpointInfo;
  }

  if (graphqlEnvArray && graphqlEnvArray.length > 0) {
    const endpointInfo = endpointUrls
      ?.filter((endpointEnv) => {
        return (!!graphqlEnvArray.find(
          (graphqlEnv) => (
            graphqlEnv.name == endpointEnv.environmentDisplayName ||
            endpointEnv.environmentName == 'control-plane' ||
            graphqlEnv.externalApiEnvName == endpointEnv.environmentName ||
            graphqlEnv.internalApiEnvName == endpointEnv.environmentName
        )));
      })
      .map((endpoint) => {
        if (endpoint.environmentDisplayName && endpoint.environmentDisplayName.includes(Constants.INTERNAL)) {
          if (endpoint.environmentDisplayName.includes(Constants.DEVELOPMENT)) {
            endpoint.environmentDisplayName = Constants.DEVELOPMENT;
          } else if (endpoint.environmentDisplayName.includes(Constants.PRODUCTION)) {
            endpoint.environmentDisplayName = Constants.PRODUCTION;
          }
        }

        if (endpoint?.URLs.https || endpoint?.URLs.wss) {
          let tmpUrl = "";
          let protocol = ""
          if (endpoint?.URLs.https) {
            tmpUrl = endpoint.URLs.https.replace(
              Constants.PROTOCOL_HTTPS,
              ''
            );
            protocol = Constants.PROTOCOL_HTTPS;
          }
          if (endpoint?.URLs.wss) {
            tmpUrl = endpoint.URLs.wss.replace(
              Constants.PROTOCOL_WSS,
              ''
            );
            protocol = Constants.PROTOCOL_WSS;
          }
          // for dynamically created environments, the orgUUID is already included in urlParts[0].
          const ORG_UUID_WITH_DOMAIN_REGEX =
            /^([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})(-).*$/;

          if (!isPdpDomainCustomizedOrg && selectedOrg?.handle !== Settings.choreoSystemOrg) {
            // for custom urls and urls with custom domains
            const environmentDisplayName = endpoint?.environmentDisplayName;
            if (!ORG_UUID_WITH_DOMAIN_REGEX.test(tmpUrl.trim().split(Constants.PATH_SEPARATOR)[0]) && environmentDisplayName !== 'Sandbox') {
              return {
                url: `${protocol}${tmpUrl}`,
                environment: endpoint?.environmentName,
                environmentDisplayName: getEnvironmentEndpointDisplayName(
                  endpoint?.environmentDisplayName
                ),
                choreoEnvironmentName: endpoint?.choreoEnvironmentName,
                accessibilityType: endpoint?.accessibilityType,
                isSandboxEndpoint: endpoint?.isSandboxEndpoint,
                isCritical: endpoint?.isCritical,
                isSandbox: endpoint?.isSandbox,
              } as EndpointInfo;
            }
          }
          if (Settings.intelligentRoutingFeatureEnabled && isLatestVersion && version.startsWith("v")) {
            const majorVersion = version.split(".")[0];
            const lastIndex = tmpUrl.lastIndexOf(majorVersion);
            const tmpUrlWithoutVersion = tmpUrl.substring(0, lastIndex);
            tmpUrl = tmpUrlWithoutVersion + majorVersion;
          }

          const urlParts = tmpUrl.trim().split(Constants.PATH_SEPARATOR);

          let hostSuffix = `${urlParts[1]}-${urlParts[0]}`;
            const ORGANIZATION_UUID_REGEX=
            /^([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})$/;
          if (ORG_UUID_WITH_DOMAIN_REGEX.test(urlParts[0]) || isPdpDomainCustomizedOrg) {
            hostSuffix = urlParts[0];
          } else {
            // Related issue for prod sandbox fix: https://github.com/wso2-enterprise/choreo/issues/26120
            const PROD_SANDBOX_URL_REGEX = /^(sandbox)\.(.*)$/;
            if (PROD_SANDBOX_URL_REGEX.test(urlParts[0])) {
              urlParts[0] = urlParts[0].replace(PROD_SANDBOX_URL_REGEX, 'prod-sandbox.$2');
            }
            hostSuffix = `${urlParts[1]}-${urlParts[0]}`;
          }
          // Set the starting position of the urlParts array to generate the context.
          // The extracted urlParts will have the format of,
          // ['<gateway-domain>','<org-uuid>','<project-handler>','<context-part-1>',...,'<context-part-n>','<version>']
          // Since we are already using 0th and 1st index to build the domain, others parts will belong to the context starting from 2nd idx.
          let urlContextStartIdx = 2;
          if (selectedOrg?.handle === Settings.choreoSystemOrg) {
            hostSuffix = Settings.choreoSystemHostSuffix;
            // The choreo system organization is a special case where we don't need the <project-handler> in the invoke url.
            // Hence, we will start from 3rd index to build the remaning context.
            // Refer https://github.com/wso2-enterprise/choreo/issues/12188 for more info.
            urlContextStartIdx = 3;
          } else if (!ORGANIZATION_UUID_REGEX.test(urlParts[1])) {
            // If the endpoint URL has the format  <Protocol>://<gateway-domain>/<project-handler>/'<context-part-1>',...,'<context-part-n>/<version>'
            // The extracted urlParts will have the format of,
            // ['<gateway-domain>','<project-handler>','<context-part-1>',...,'<context-part-n>','<version>']
            // Build remaining context starting from the 1st index
            urlContextStartIdx = 1;
          }

          const gwURL = `${protocol}${hostSuffix}/${urlParts
            .slice(urlContextStartIdx)
            .join(Constants.PATH_SEPARATOR)}`;
          
          return {
            url: gwURL,
            environment: endpoint?.environmentName,
            environmentDisplayName: getEnvironmentEndpointDisplayName(
              endpoint?.environmentDisplayName
            ),
            choreoEnvironmentName: endpoint?.choreoEnvironmentName,
            accessibilityType: endpoint?.accessibilityType,
            isSandboxEndpoint: endpoint?.isSandboxEndpoint,
            isCritical: endpoint?.isCritical,
            isSandbox: endpoint?.isSandbox,
          } as EndpointInfo;
        }
        return null;
      })
      .filter((endpoint) => endpoint !== null);
    return endpointInfo;
  }

  return [];
};

const getEnvironmentEndpointDisplayName = (environmentName: string): string => {
  switch (environmentName.toLowerCase()) {
    case "production-us-east-azure":
      return "Production";
    case "production-eu-north-azure":
      return "Production";
    case "development-us-east-azure":
      return "Development";
    case "development-eu-north-azure":
      return "Development";
    case "production-sandbox-us-east-azure":
      return "Sandbox";
    case "production-sandbox-eu-north-azure":
      return "Sandbox";
    default:
      return environmentName;
  }
}

export const getIDPsEndpoint = (): string => {
  const apimHostname = Settings.apimApi;
  const appResource = Settings.devportalContext + Constants.RESOURCE_IDPS;

  return new URL(appResource, apimHostname).toString();
};

export const getAddExternalUserToOrgEndpoint = (
  organizationHandle: string
): string => {
  const apiServiceHost = Settings.apiServiceHost;
  const resource =
    PATH_SEPARATOR +
    'user-mgt/1.0.0/orgs' +
    PATH_SEPARATOR +
    organizationHandle +
    PATH_SEPARATOR +
    'self-subscribe';
  return new URL(resource, apiServiceHost).toString();
};

export const getChoreoUserInfoEndpoint = (
  organizationHandle: string,
  userIdpId: string
): string => {
  const apiServiceHost = Settings.apiServiceHost;
  const resource = `/user-mgt/1.0.0/orgs/${organizationHandle}/users/${userIdpId}`;
  return new URL(resource, apiServiceHost).toString();
};

/**
 * Function to get the endpoint to get the application keys map.
 * @param applicationId The application id
 * @returns the construction endpoint
 */
export const getApplicationKeysMapEndpoint = (applicationId: string): string => {
  const apimHostname = Settings.apimApi;
  const appResource = `${Settings.devportalContext}/applications/${applicationId}${Constants.MAP_APPLICATION_KEYS}`;
  return new URL(appResource, apimHostname).toString();
};

/**
 * Method to get the accessibility of the api.
 * @param api The API object
 * @returns True if the api is an internal api, false otherwise
 */
export const getIsInternalAPI = (api?: Api): boolean => {
  return api?.additionalProperties?.find(property =>
    property.name === 'accessibility')?.value === 'internal';
};

/**
 * Method to get the accessibility of the api.
 * @param orgName Selected organization handle
 * @returns True if the given orgName is listed in selfSignupEnabledOrgs, false otherwise
 */
export const isSelfSignupEnabled = (orgName: string): boolean => {
  return Settings.selfSignupEnabledOrgs.includes(orgName);
};

/**
 * Method to get check whether selected api is a graphql api
 * @param api The API object
 * @returns True if the api is a graphql api, false otherwise
 */
export const isGraphqlApi = (api?: Api): boolean => {
  return (
    api?.additionalProperties?.some(
      (property) =>
        (property.name === "applicationType" && property.value === "graphql") ||
        (property.name === "endpointType" && property.value === "GraphQL")
    ) || false
  );
};

/**
 * Method to get the value of the 'endpointType' property from the API object
 * @param api The API object
 * @returns The value of 'endpointType' if it exists, undefined otherwise
 */
export const getAPIType = (api?: Api): string => {
  if (isGraphqlApi(api)) {
    return Constants.API_TYPE_GRAPHQL;
  }
  const property = api?.additionalProperties?.find(
    (property) => property.name === "endpointType"
  );
  return property?.value || Constants.API_TYPE_HTTP;
};

export const isThirdPartyApi = (api?: Api): boolean => {
  return api?.advertiseInfo?.advertised || false;
}

export const truncateTextMiddle = (
  fullStr: string,
  strLen: number,
  separator_?: string
) => {
  if (fullStr.length <= strLen) return fullStr;

  const separator = separator_ || '...';

  const sepLen = separator.length;
  const charsToShow = strLen - sepLen;
  const frontChars = Math.ceil(charsToShow / 2);
  const backChars = Math.floor(charsToShow / 2);

  return (
    fullStr.substring(0, frontChars) +
    separator +
    fullStr.substring(fullStr.length - backChars)
  );
};

export const mapAndAddKeyToKeyList = (key: Key | undefined | string, keyList: KeyList | null): KeyList => {
  if (key === undefined || typeof key === "string") {
    return { list: [], count: 0 };
  }
  // Append key into keyList if key.keyType is not present on keyList
  // else replace the key with the key which has the same type
  const newKeyList: Key[] = keyList ? [...keyList.list] : [];
  const keyType = key.keyType;
  const keyIndex = newKeyList.findIndex((key) => key.keyType === keyType);
  if (keyIndex === -1) {
    newKeyList.push(key);
  } else {
    newKeyList[keyIndex] = key;
  }
  return { list: newKeyList, count: newKeyList.length };
}

export const isCredentialMenuAccessible = (allowedScopes: string): boolean => {
  return !!allowedScopes && allowedScopes.includes(SUBSCRIBE_SCOPE) && (
      allowedScopes.includes(SANDBOX_KEY_MANAGE_SCOPE) || allowedScopes.includes(PRODUCTION_KEY_MANAGE_SCOPE)
  );
}

export const isSandboxKeyGenerationAccessible = (allowedScopes: string): boolean => {
  return !!allowedScopes && allowedScopes.includes(SANDBOX_KEY_MANAGE_SCOPE);
}

export const isProductionKeyGenerationAccessible = (allowedScopes: string): boolean => {
  return !!allowedScopes && allowedScopes.includes(PRODUCTION_KEY_MANAGE_SCOPE);
}

export const isContractsAccessible = (allowedScopes: string): boolean => {
  return !!allowedScopes && allowedScopes.includes(SUBSCRIBE_SCOPE);
}

export const getEndpointUrls = (api: Api): EndpointURL[] => {
  if (api?.version?.startsWith("v") && api?.versionInfo) {
    return api.versionInfo.majorVersionEndpointUrls;
  }
  return api?.endpointURLs;
};

export const getVisibilityFromAccessibilityType = (
  accessibilityType: string
): string => {
  if (accessibilityType === "external") {
    return "Public";
  } else if (accessibilityType === "internal") {
    return "Organization";
  } else {
    return "";
  }
};

export const isAdminUser = (allowedScopes: string): boolean => {
  return !!allowedScopes && allowedScopes.includes(ADMIN_SCOPE);
}
